import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  Spinner,
  Tabs,
} from "../../../design-system";
import {
  listWorkdayCorrections,
} from "../../../helpers/kronaly_helper";
import { formatDateOnly, formatInstant } from "../../../helpers/datetime";
import DetalleCorreccionDrawer from "./DetalleCorreccionDrawer";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const STATUS_LABEL = {
  pending: { label: "Pendiente", tone: "warning" },
  approved: { label: "Aprobada", tone: "success" },
  rejected: { label: "Rechazada", tone: "danger" },
  cancelled: { label: "Cancelada", tone: "neutral" },
};

export function BandejaCorrecciones({ isManager, currentUserId, currentEmployeeProfileId, tz }) {
  const tabs = useMemo(() => {
    if (isManager) {
      return [
        { id: "to-approve", label: "Por aprobar" },
        { id: "mine", label: "Mis solicitudes" },
        { id: "all", label: "Todas" },
      ];
    }
    return [{ id: "mine", label: "Mis solicitudes" }];
  }, [isManager]);

  const [subTab, setSubTab] = useState(tabs[0].id);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ "to-approve": 0, mine: 0, all: 0 });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (scope) => {
    setLoading(true);
    setError(null);
    try {
      const r = await listWorkdayCorrections({ scope });
      setItems(unwrap(r) || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudieron cargar las correcciones.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(subTab); }, [load, subTab]);

  // Carga de contadores para los badges de los tabs (solo si manager).
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    listWorkdayCorrections({ scope: "to-approve" })
      .then((r) => {
        if (!cancelled) setCounts((c) => ({ ...c, "to-approve": (unwrap(r) || []).length }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isManager]);

  const tabsWithCounts = useMemo(() =>
    tabs.map((t) => ({ ...t, count: t.id === "to-approve" ? counts["to-approve"] : undefined })),
    [tabs, counts]);

  const handleAfterAction = useCallback(async () => {
    setSelected(null);
    await load(subTab);
    if (isManager) {
      try {
        const r = await listWorkdayCorrections({ scope: "to-approve" });
        setCounts((c) => ({ ...c, "to-approve": (unwrap(r) || []).length }));
      } catch {}
    }
  }, [load, subTab, isManager]);

  return (
    <Card padded={false}>
      <div style={{ padding: "10px 16px 0" }}>
        <Tabs tabs={tabsWithCounts} value={subTab} onChange={setSubTab} />
      </div>

      {error && <div style={{ padding: 16 }}><Alert tone="danger">{error}</Alert></div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center" }}><Spinner size={18} /></div>
      ) : items.length === 0 ? (
        <div style={{ padding: 24 }}>
          <EmptyState
            icon="Inbox"
            title={subTab === "to-approve" ? "Sin correcciones por aprobar" : "Sin correcciones"}
            description={subTab === "to-approve"
              ? "Cuando un empleado solicite corregir una jornada, aparecerá aquí."
              : "Aún no has solicitado ninguna corrección de jornada."}
          />
        </div>
      ) : (
        <TablaCorrecciones
          items={items}
          currentUserId={currentUserId}
          currentEmployeeProfileId={currentEmployeeProfileId}
          onOpen={setSelected}
          tz={tz}
        />
      )}

      <DetalleCorreccionDrawer
        correction={selected}
        tz={tz}
        currentUserId={currentUserId}
        currentEmployeeProfileId={currentEmployeeProfileId}
        isManager={isManager}
        onClose={() => setSelected(null)}
        onAfterAction={handleAfterAction}
      />
    </Card>
  );
}

function TablaCorrecciones({ items, currentUserId, currentEmployeeProfileId, onOpen, tz }) {
  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "120px 110px 110px 160px 180px 1fr",
        padding: "10px 20px",
        background: "var(--kr-neutral-50)",
        borderBottom: "1px solid var(--kr-border-subtle)",
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontWeight: 600,
        color: "var(--kr-fg-muted)",
        gap: 0,
      }}>
        <div>Fecha jornada</div>
        <div>Estado</div>
        <div>Origen</div>
        <div>Solicitada por</div>
        <div>Empleado afectado</div>
        <div>Resumen</div>
      </div>

      {items.map((c) => {
        const status = STATUS_LABEL[c.status] || { label: c.status, tone: "neutral" };
        const isSelf = c.requested_origin === "self";
        const requestedByMe = c.requested_by === currentUserId;
        const affectsMe = c.employee_profile_id === currentEmployeeProfileId;

        let originLabel;
        if (isSelf) {
          originLabel = requestedByMe ? "Auto-corrección" : "Auto-corrección";
        } else {
          originLabel = requestedByMe ? "Inicié yo" : (affectsMe ? "Sobre mí" : "Sobre otro");
        }

        const segments = c.proposed_segments || [];
        const totalMin = segments.reduce((acc, s) => {
          if (!s.clock_in_at || !s.clock_out_at) return acc;
          return acc + Math.max(0, Math.round((new Date(s.clock_out_at) - new Date(s.clock_in_at)) / 60000));
        }, 0);
        const breakMin = (c.proposed_breaks || []).reduce((acc, b) => {
          if (!b.start_at || !b.end_at) return acc;
          return acc + Math.max(0, Math.round((new Date(b.end_at) - new Date(b.start_at)) / 60000));
        }, 0);
        const net = Math.max(0, totalMin - breakMin);

        const requesterLabel = requestedByMe
          ? "Tú"
          : (c.requested_by_name || c.requested_by?.slice(0, 8) + "…");
        const employeeLabel = affectsMe
          ? "Yo"
          : (c.employee_name || c.employee_profile_id?.slice(0, 8) + "…");

        return (
          <div
            key={c.id}
            onClick={() => onOpen(c)}
            style={{
              display: "grid",
              gridTemplateColumns: "120px 110px 110px 160px 180px 1fr",
              padding: "12px 20px",
              borderBottom: "1px solid var(--kr-border-subtle)",
              cursor: "pointer",
              alignItems: "center",
              fontSize: 13,
              transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--kr-bg-subtle)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
          >
            <div style={{ fontFamily: "var(--kr-font-mono)", fontWeight: 600, color: "var(--kr-fg-strong)" }}>
              {formatDateOnly(c.work_date)}
            </div>
            <div>
              <Badge tone={status.tone} dot>{status.label}</Badge>
            </div>
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{originLabel}</div>
            <div style={{ fontSize: 12, color: "var(--kr-fg-strong)" }}>
              <div style={{ fontWeight: requestedByMe ? 600 : 500 }}>{requesterLabel}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 1 }}>
                {formatInstant(c.requested_at, tz)}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: affectsMe ? 600 : 500 }}>
              {employeeLabel}
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--kr-fg-muted)", alignItems: "center" }}>
              <span style={{ fontFamily: "var(--kr-font-mono)" }}>
                Propuesto: {Math.floor(net / 60)}h {net % 60}m
              </span>
              {c.note && (
                <span style={{
                  fontStyle: "italic",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}>
                  “{c.note}”
                </span>
              )}
              <Icon name="ChevronRight" size={14} style={{ color: "var(--kr-fg-subtle)", flexShrink: 0 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default BandejaCorrecciones;
